import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Truck,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import DoubleScrollbar from '../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../components/UI/Modal';
import Input from '../components/UI/Input';
import { formatDateTime } from '../utils/formatDateTime';
import { useSettings } from '../contexts/SettingsContext';
import Button from '../components/UI/Button';
import { DeletionProtectionModal } from '../components/UI';
import { useDeletionProtection } from '../hooks/useDeletionProtection';

interface Supplier {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  contactPerson?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const SuppliersPage: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    contactPerson: '',
    details: '',
    isActive: true,
  });

  const { settings } = useSettings();

  // Deletion protection
  const {
    showDeletionProtectionModal,
    deletionProtectionData,
    checkAndDelete,
    closeDeletionProtectionModal
  } = useDeletionProtection();

  // Add URL parameter handling for search
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Handle URL search parameter
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchTerm(urlSearch);
    }
  }, [searchParams, setSearchTerm]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        // Clear cache on component mount to ensure fresh data
        apiService.invalidateSuppliersCache();
        await fetchSuppliers(true);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to fetch suppliers');
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const fetchSuppliers = async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      let response;
      if (forceRefresh) {
        // For force refresh, bypass cache completely
        console.log('Force refreshing suppliers, bypassing cache');
        response = await apiService.forceRefresh('/suppliers');
      } else {
        response = await apiService.get('/suppliers');
      }
      
      console.log('Fetched suppliers:', response.data.suppliers?.length || 0, 'suppliers');
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  // Soft refresh listener
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateSuppliersCache();
      fetchSuppliers(true);
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const handleAddSupplier = async () => {
    try {
      const response = await apiService.post('/suppliers', newSupplier);
      toast.success('Supplier added successfully');
      
      // Immediately clear suppliers cache and force refresh
      apiService.invalidateSuppliersCache();
      
      setShowAddModal(false);
      setNewSupplier({
        name: '',
        email: '',
        phone: '',
        address: '',
        contactPerson: '',
        details: '',
        isActive: true,
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchSuppliers(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to add supplier');
    }
  };

  const handleEditSupplier = async () => {
    if (!editingSupplier) return;
    
    try {
      const response = await apiService.put(`/suppliers/${editingSupplier._id}`, editingSupplier);
      toast.success('Supplier updated successfully');
      
      // Immediately clear suppliers cache and force refresh
      apiService.invalidateSuppliersCache();
      
      setShowEditModal(false);
      setEditingSupplier(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchSuppliers(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to update supplier');
    }
  };

  const handleDeleteSupplier = async (supplierId: string) => {
    const supplier = suppliers.find(s => s._id === supplierId);
    if (!supplier) return;

    const deleteFunction = async () => {
      try {
        const response = await apiService.delete(`/suppliers/${supplierId}`);
        toast.success('Supplier deleted successfully');
        
        // Immediately clear suppliers cache and force refresh
        apiService.invalidateSuppliersCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchSuppliers(true);
        }, 100);
      } catch (error) {
        console.error('Error deleting supplier:', error);
        toast.error('Failed to delete supplier');
      }
    };

    await checkAndDelete('suppliers', supplierId, supplier.name, deleteFunction);
  };

  const openEditModal = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setShowEditModal(true);
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || supplier.isActive === (statusFilter === 'active');
    
    return matchesSearch && matchesStatus;
  });

  const exportSuppliers = async () => {
    try {
      await apiService.download('/suppliers/export/excel', 'suppliers.xlsx');
      toast.success('Suppliers exported successfully');
    } catch (error) {
      console.error('Error exporting suppliers:', error);
      toast.error('Failed to export suppliers');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text)] font-[var(--font-family)]">Suppliers</h1>
          <p className="text-[var(--color-text)] font-[var(--font-family)]">
            Manage your supplier information
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { apiService.invalidateSuppliersCache(); fetchSuppliers(true); }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Supplier
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  // Update URL with search parameter
                  if (value) {
                    setSearchParams({ search: value });
                  } else {
                    setSearchParams({});
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportSuppliers}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Suppliers Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Contact Person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Truck className="w-5 h-5 text-[var(--color-primary)] mr-2" />
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {supplier.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {supplier.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {supplier.phone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {supplier.contactPerson || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                    {supplier.address || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${supplier.isActive ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{supplier.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(supplier.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(supplier)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSupplier(supplier._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredSuppliers.length === 0 && (
          <div className="text-center py-12">
            <Truck className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No suppliers found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first supplier.'}
            </p>
          </div>
        )}
      </div>

      {/* Add Supplier Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Supplier"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newSupplier.name}
            onChange={e => setNewSupplier({ ...newSupplier, name: e.target.value })}
            placeholder="Enter supplier name"
            required
          />
          <Input
            label="Email"
            type="email"
            value={newSupplier.email}
            onChange={e => setNewSupplier({ ...newSupplier, email: e.target.value })}
            placeholder="Enter supplier email"
            required
          />
          <Input
            label="Phone"
            value={newSupplier.phone}
            onChange={e => setNewSupplier({ ...newSupplier, phone: e.target.value })}
            placeholder="Enter supplier phone"
            required
          />
          <Input
            label="Contact Person"
            value={newSupplier.contactPerson}
            onChange={e => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
            placeholder="Enter contact person"
            required
          />
          <Input
            label="Address"
            value={newSupplier.address}
            onChange={e => setNewSupplier({ ...newSupplier, address: e.target.value })}
            placeholder="Enter supplier address"
            required
          />
          <Input
            label="Details"
            value={newSupplier.details}
            onChange={e => setNewSupplier({ ...newSupplier, details: e.target.value })}
            placeholder="Enter supplier details"
            required
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newSupplier.isActive ? 'active' : 'inactive'}
              onChange={e => setNewSupplier({ ...newSupplier, isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddSupplier}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Add Supplier
          </button>
        </div>
      </Modal>

      {/* Edit Supplier Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Supplier"
      >
        {editingSupplier && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingSupplier.name}
              onChange={e => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
              placeholder="Enter supplier name"
              required
            />
            <Input
              label="Email"
              type="email"
              value={editingSupplier.email || ''}
              onChange={e => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
              placeholder="Enter supplier email"
            />
            <Input
              label="Phone"
              value={editingSupplier.phone || ''}
              onChange={e => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
              placeholder="Enter supplier phone"
            />
            <Input
              label="Contact Person"
              value={editingSupplier.contactPerson || ''}
              onChange={e => setEditingSupplier({ ...editingSupplier, contactPerson: e.target.value })}
              placeholder="Enter contact person"
            />
            <Input
              label="Address"
              value={editingSupplier.address || ''}
              onChange={e => setEditingSupplier({ ...editingSupplier, address: e.target.value })}
              placeholder="Enter supplier address"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingSupplier.isActive ? 'active' : 'inactive'}
                onChange={e => setEditingSupplier({ ...editingSupplier, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowEditModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditSupplier}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Update Supplier
          </button>
        </div>
      </Modal>

      {/* Deletion Protection Modal */}
      <DeletionProtectionModal
        isOpen={showDeletionProtectionModal}
        onClose={closeDeletionProtectionModal}
        entityName={deletionProtectionData?.entityName || ''}
        entityType={deletionProtectionData?.entityType || ''}
        dependencies={deletionProtectionData?.dependencies || []}
        onViewDetails={(dependency) => {
          // Navigate to the dependency details
          console.log('View details for:', dependency);
          // You can implement navigation here if needed
        }}
      />
    </div>
  );
};

export default SuppliersPage; 